import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const sections = [
  {
    id: 'overview',
    icon: 'ri-eye-line',
    title: '1. Overview',
    content: [
      'Faasleyfoods Pvt. Ltd. ("Faasley Foods", "we", "us", or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or interact with us.',
      'By using our website, you consent to the data practices described in this policy. If you do not agree with the terms of this Privacy Policy, please do not access the site.',
    ],
  },
  {
    id: 'collection',
    icon: 'ri-database-2-line',
    title: '2. Information We Collect',
    content: [
      'We may collect the following types of information:',
    ],
    list: [
      { label: 'Personal Information', desc: 'Name, email address, phone number, and delivery address when you place an order or fill out a contact form.' },
      { label: 'Usage Data', desc: 'Information about how you use our website, including pages visited, time spent, and referring URLs.' },
      { label: 'Device Information', desc: 'Browser type, operating system, IP address, and device identifiers for analytics and security purposes.' },
      { label: 'Communication Data', desc: 'Messages you send us via contact forms, email, or WhatsApp.' },
    ],
  },
  {
    id: 'use',
    icon: 'ri-settings-3-line',
    title: '3. How We Use Your Information',
    content: [
      'We use the information we collect for the following purposes:',
    ],
    bullets: [
      'To process and fulfil your orders and send order confirmations',
      'To respond to your inquiries, questions, and customer service requests',
      'To send promotional communications (only with your consent)',
      'To improve our website, products, and services',
      'To comply with legal obligations and enforce our Terms & Conditions',
      'To detect and prevent fraud or other illegal activities',
    ],
  },
  {
    id: 'sharing',
    icon: 'ri-share-line',
    title: '4. Sharing Your Information',
    content: [
      'We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following limited circumstances:',
    ],
    bullets: [
      'With delivery and logistics partners to fulfil your orders',
      'With payment processors to complete transactions securely',
      'With analytics providers (e.g., Google Analytics) to understand website usage',
      'When required by law, court order, or government authority',
      'To protect the rights, property, or safety of Faasley Foods, our customers, or others',
    ],
  },
  {
    id: 'cookies',
    icon: 'ri-spy-line',
    title: '5. Cookies & Tracking',
    content: [
      'Our website may use cookies and similar tracking technologies to enhance your browsing experience. Cookies are small data files stored on your device that help us remember your preferences and understand how you use our site.',
      'You can control cookie settings through your browser preferences. Disabling cookies may affect some functionality of the website. We use cookies for session management, analytics, and personalisation purposes only.',
    ],
  },
  {
    id: 'security',
    icon: 'ri-shield-keyhole-line',
    title: '6. Data Security',
    content: [
      'We implement appropriate technical and organisational measures to protect your personal information against unauthorised access, alteration, disclosure, or destruction. However, no method of transmission over the internet or electronic storage is 100% secure.',
      'We encourage you to use strong passwords and to be cautious when sharing personal information online. If you believe your information has been compromised, please contact us immediately.',
    ],
  },
  {
    id: 'retention',
    icon: 'ri-time-line',
    title: '7. Data Retention',
    content: [
      'We retain your personal information only for as long as necessary to fulfil the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.',
      'Order and transaction records may be retained for up to 7 years for accounting and legal compliance purposes. You may request deletion of your personal data at any time, subject to legal obligations.',
    ],
  },
  {
    id: 'rights',
    icon: 'ri-user-settings-line',
    title: '8. Your Rights',
    content: [
      'You have the following rights regarding your personal information:',
    ],
    bullets: [
      'Right to access — request a copy of the personal data we hold about you',
      'Right to correction — request correction of inaccurate or incomplete data',
      'Right to deletion — request deletion of your personal data',
      'Right to opt-out — unsubscribe from marketing communications at any time',
      'Right to data portability — receive your data in a structured, machine-readable format',
    ],
  },
  {
    id: 'children',
    icon: 'ri-parent-line',
    title: '9. Children\'s Privacy',
    content: [
      'Our website is not directed to children under the age of 13. We do not knowingly collect personal information from children. If you believe we have inadvertently collected information from a child, please contact us and we will promptly delete it.',
    ],
  },
  {
    id: 'changes',
    icon: 'ri-refresh-line',
    title: '10. Changes to This Policy',
    content: [
      'We may update this Privacy Policy from time to time to reflect changes in our practices or for legal, operational, or regulatory reasons. We will notify you of any significant changes by updating the "Last Updated" date at the top of this page.',
      'We encourage you to review this Privacy Policy periodically to stay informed about how we protect your information.',
    ],
  },
  {
    id: 'contact',
    icon: 'ri-mail-line',
    title: '11. Contact Us',
    content: [
      'If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:',
    ],
    contact: true,
  },
];

export default function PrivacyPage() {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-privacy';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${SITE_URL}/privacy#webpage`,
      url: `${SITE_URL}/privacy`,
      name: 'Privacy Policy | Faasley Foods',
      description: 'Read the Privacy Policy for Faasley Foods. Learn how we collect, use, and protect your personal information.',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Privacy Policy', item: `${SITE_URL}/privacy` },
        ],
      },
    });
    document.head.appendChild(script);
    document.title = 'Privacy Policy | Faasley Foods';
    return () => {
      const el = document.getElementById('schema-privacy');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <div className="pt-[200px] md:pt-[220px] pb-16 bg-amber-50/60 border-b border-amber-100">
        <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20">
          <div className="flex items-center gap-2 text-sm text-stone-400 mb-6">
            <Link to="/" className="hover:text-amber-600 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-stone-600">Privacy Policy</span>
          </div>
          <div className="max-w-3xl">
            <span className="inline-block bg-amber-100 text-amber-700 text-xs font-bold uppercase tracking-[0.2em] px-4 py-1.5 rounded-full mb-5">
              Legal
            </span>
            <h1 className="font-display text-4xl md:text-5xl font-bold text-stone-900 mb-4 leading-tight">
              Privacy Policy
            </h1>
            <p className="text-stone-500 text-base leading-relaxed max-w-xl">
              Your privacy matters to us. This policy explains how Faasley Foods collects, uses, and protects your personal information. Last updated: April 2026.
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20 py-16 md:py-24">
        <div className="grid lg:grid-cols-[260px_1fr] gap-12 lg:gap-16 items-start">

          {/* Sticky Table of Contents */}
          <aside className="hidden lg:block sticky top-28">
            <p className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-4">On This Page</p>
            <nav className="space-y-1">
              {sections.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="flex items-center gap-2.5 text-sm text-stone-400 hover:text-amber-600 py-1.5 transition-colors group"
                >
                  <span className="w-0 group-hover:w-2 h-px bg-amber-500 transition-all flex-shrink-0"></span>
                  {s.title.replace(/^\d+\.\s/, '')}
                </a>
              ))}
            </nav>
            <div className="mt-8 p-5 bg-amber-50 rounded-2xl border border-amber-100">
              <p className="text-xs font-bold text-stone-700 mb-2">Data Request?</p>
              <p className="text-xs text-stone-400 leading-relaxed mb-3">
                To access, correct, or delete your data, reach out to us directly.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-600 hover:text-amber-700 transition-colors"
              >
                Contact Us <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </aside>

          {/* Main Content */}
          <main>
            <div className="space-y-12">
              {sections.map((section) => (
                <section key={section.id} id={section.id} className="scroll-mt-28">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-amber-50 flex-shrink-0">
                      <i className={`${section.icon} text-amber-500 text-base`}></i>
                    </div>
                    <h2 className="font-display text-xl font-bold text-stone-900">{section.title}</h2>
                  </div>
                  <div className="space-y-4 pl-12">
                    {section.content.map((para, i) => (
                      <p key={i} className="text-stone-500 text-sm leading-relaxed">{para}</p>
                    ))}
                    {'list' in section && section.list && (
                      <div className="space-y-3 mt-2">
                        {section.list.map((item) => (
                          <div key={item.label} className="flex items-start gap-3 bg-stone-50 rounded-xl p-4 border border-stone-100">
                            <i className="ri-checkbox-circle-line text-amber-500 text-base mt-0.5 flex-shrink-0"></i>
                            <div>
                              <span className="font-semibold text-stone-800 text-sm">{item.label}: </span>
                              <span className="text-stone-500 text-sm">{item.desc}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {'bullets' in section && section.bullets && (
                      <ul className="space-y-2 mt-2">
                        {section.bullets.map((bullet, i) => (
                          <li key={i} className="flex items-start gap-2.5 text-sm text-stone-500">
                            <i className="ri-arrow-right-s-line text-amber-500 text-base mt-0.5 flex-shrink-0"></i>
                            {bullet}
                          </li>
                        ))}
                      </ul>
                    )}
                    {section.contact && (
                      <div className="mt-4 bg-stone-50 rounded-2xl p-6 border border-stone-100 space-y-3">
                        {[
                          { icon: 'ri-building-line', label: 'Company', value: 'Faasleyfoods Pvt. Ltd.' },
                          { icon: 'ri-mail-line', label: 'Email', value: 'pari211284@gmail.com' },
                          { icon: 'ri-phone-line', label: 'Phone', value: '+91 8800889959' },
                          { icon: 'ri-map-pin-line', label: 'Country', value: 'India' },
                        ].map((item) => (
                          <div key={item.label} className="flex items-center gap-3">
                            <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                              <i className={`${item.icon} text-amber-500 text-sm`}></i>
                            </div>
                            <span className="text-xs text-stone-400 w-16 flex-shrink-0">{item.label}</span>
                            <span className="text-sm text-stone-700 font-medium">{item.value}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="mt-10 h-px bg-stone-100"></div>
                </section>
              ))}
            </div>

            {/* Bottom CTA */}
            <div className="mt-12 bg-amber-50 rounded-3xl p-8 border border-amber-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <div>
                <p className="font-semibold text-stone-900 mb-1">Also read our Terms &amp; Conditions</p>
                <p className="text-sm text-stone-400">Understand the rules for using our website and services.</p>
              </div>
              <Link
                to="/terms"
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold px-6 py-3 rounded-full text-sm transition-colors whitespace-nowrap"
              >
                Terms &amp; Conditions <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
}
